/** Les quatre langues servies par l'application. */
export const SUPPORTED_LANGS = ['fr', 'en', 'de', 'es'] as const;

/** Une langue servie. */
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

/** Langue de repli : celle dans laquelle tous les libellés existent toujours. */
export const DEFAULT_LANG: SupportedLang = 'fr';

/** Clé de mémorisation du choix explicite du visiteur, propre à cet appareil. */
export const LANG_STORAGE_KEY = 'kc-terrain-lang';

/**
 * Étiquettes de locale pour le formatage des dates et des nombres.
 *
 * @remarks
 * Mêmes valeurs que le `getMaterialLocale` de KeepControlWeb.Front, `en-US` compris : les deux
 * applications doivent formater une date de la même façon pour un même utilisateur.
 */
const LOCALE_TAGS: Record<SupportedLang, string> = {
  fr: 'fr-FR',
  en: 'en-US',
  de: 'de-DE',
  es: 'es-ES',
};

/**
 * Vrai si le code donné est l'une des quatre langues servies.
 *
 * @param value Code à tester.
 */
export function isSupportedLang(value: string | null | undefined): value is SupportedLang {
  return !!value && (SUPPORTED_LANGS as readonly string[]).includes(value);
}

/**
 * Extrait la langue de base d'un code de langue KEEP CONTROL.
 *
 * @param code Code brut, par exemple `de-67`, `fr` ou `null`.
 * @returns L'une des quatre langues servies, {@link DEFAULT_LANG} à défaut.
 * @remarks
 * `AspNetUsers.LangueCode` a le format `{lang}-{clientId}` — le suffixe est un **identifiant
 * client**, pas une région BCP 47. Passer le code brut à `Intl` lève `RangeError: invalid language
 * tag` : tout ce qui touche au formatage doit passer par ici, puis par {@link getLocaleTag}.
 * Certains chemins historiques écrivent aussi un simple `fr` : les deux formes sont acceptées.
 */
export function getBaseLang(code: string | null | undefined): SupportedLang {
  if (!code) return DEFAULT_LANG;

  const base = code.split('-')[0].toLowerCase();
  return isSupportedLang(base) ? base : DEFAULT_LANG;
}

/**
 * Étiquette de locale à donner à `Intl` ou à `formatDate` pour une langue servie.
 *
 * @param lang Langue servie.
 */
export function getLocaleTag(lang: SupportedLang): string {
  return LOCALE_TAGS[lang];
}

/**
 * Langue déduite des préférences du navigateur.
 *
 * @returns L'une des quatre langues servies, {@link DEFAULT_LANG} à défaut.
 * @remarks
 * La fiche s'ouvre en scannant une étiquette : son lecteur n'est pas choisi. Un intervenant
 * étranger doit donc obtenir sa langue sans rien régler.
 */
export function resolveBrowserLang(): SupportedLang {
  const candidates = [...(navigator.languages ?? []), navigator.language];

  for (const candidate of candidates) {
    const base = getBaseLangStrict(candidate);
    if (base) return base;
  }

  return DEFAULT_LANG;
}

/** Comme {@link getBaseLang}, mais rend `null` au lieu du français quand la langue n'est pas servie. */
function getBaseLangStrict(code: string | null | undefined): SupportedLang | null {
  if (!code) return null;

  const base = code.split('-')[0].toLowerCase();
  return isSupportedLang(base) ? base : null;
}
